// src/services/api/chats.ts
import { apiClient, ApiResponse } from './index';

export interface ChatMessage {
  message_id: string;
  direction: 'inbound' | 'outbound';
  customer_phone: string;
  message_type: 'text' | 'image' | 'document';
  content: string;
  status: string;
  timestamp: string;
}

export interface ChatSession {
  id: number;
  customer_phone: string;
  customer_name: string;
  mode: 'auto' | 'manual';
  status: 'active' | 'closed';
  requires_attention: boolean;
  unknown_count: number;
  assigned_to: number | null;
  assigned_to_username: string | null;
  last_message: {
    content: string;
    timestamp: string;
    direction: 'inbound' | 'outbound';
    message_type: string;
  } | null;
  unread_count: number;
  updated_at: string;
  created_at: string;
}

export interface ChatSessionDetail extends ChatSession {
  customer_info: {
    id: string;
    name: string;
    phone: string;
    status: string;
    orders_count: number;
    total_spent: number;
  } | null;
}

// Кастомный формат (наш backend)
export interface ChatsResponse {
  chats: ChatSession[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// Стандартный формат Django REST Framework
export interface DRFChatsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ChatSession[];
}

// Универсальный тип для обработки обоих форматов
export type ChatsApiResponse = ChatsResponse | DRFChatsResponse | ChatSession[];

// Вспомогательная функция для определения типа ответа
export function isDRFResponse(data: any): data is DRFChatsResponse {
  return data && 'results' in data && 'count' in data;
}

export function isCustomResponse(data: any): data is ChatsResponse {
  return data && 'chats' in data && 'pagination' in data;
}

export const chatsApi = {
  // Получить список чатов с фильтрами
  getChats: (params?: {
    limit?: number;
    offset?: number;
    requires_attention?: boolean;
    mode?: 'auto' | 'manual';
    search?: string;
  }) =>
    apiClient.get<ApiResponse<ChatsApiResponse>>('/api/admin/chats/', { params }),

  // Получить детали чата
  getChatDetail: (chatId: number) =>
    apiClient.get<ApiResponse<ChatSessionDetail>>(`/api/admin/chats/${chatId}/`),

  // Получить историю сообщений
  getChatMessages: (chatId: number, limit: number = 100) =>
    apiClient.get<ApiResponse<ChatMessage[] | { results: ChatMessage[] }>>(
      `/api/admin/chats/${chatId}/messages/`,
      { params: { limit } }
    ),

  // Отправить сообщение (только admin)
  sendMessage: (chatId: number, message: string) =>
    apiClient.post<ApiResponse<{ message_id: string; message: ChatMessage }>>(
      `/api/admin/chats/${chatId}/send_message/`,
      { message }
    ),

  // Взять чат в ручной режим
  takeControl: (chatId: number) =>
    apiClient.post<ApiResponse<{ mode: string; chat: ChatSession }>>(
      `/api/admin/chats/${chatId}/take_control/`
    ),

  // Вернуть чат боту
  releaseControl: (chatId: number) =>
    apiClient.post<ApiResponse<{ mode: string; chat: ChatSession }>>(
      `/api/admin/chats/${chatId}/release_control/`
    ),
};